import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { LayersIcon, ClockIcon, ArrowRightIcon, PlayIcon, TriangleAlertIcon, TerminalIcon, BookIcon, ChestIcon, BitsIcon } from '../components/Icons';
import { ALL_TOPICS, isPaper2Topic, isPaper2Accessible, isTopicAccessible, isTheoryAccessible } from '../curriculumConfig';
import UpgradeOptions from '../components/UpgradeOptions';
import { THEME_A } from '../themeAConfig';
import {
  STUB_PLAYER,
  MASTERY_DECAY_THRESHOLD,
  calcThemeBProgress,
  deriveNeedsReview,
} from '../gamificationConfig';
import GlobalPlayerCard from '../components/GlobalPlayerCard';
import TrackPicker      from '../components/TrackPicker';
import TheLocker        from '../components/TheLocker';
import Flashcards       from './Flashcards';
import Quiz             from './Quiz';
import SqlExercise      from './SqlExercise';
import { authHeaders }  from '../authToken';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Greeting ──────────────────────────────────────────────────────────────────

function timeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// ── Zone tiles — the three sections, as unmissable launcher cards ─────────────

function ZoneTiles({ activeTab, onSelect, themeBProgress, themeAProgress, showThemeA, exercisesDone, onOpenBits }) {
  const tiles = [
    {
      id: 'coding', label: 'Coding Labs', Icon: TerminalIcon,
      desc: 'Write real Python & Java against the syllabus',
      pct: Math.round(themeBProgress), barColor: 'var(--success)',
    },
    ...(showThemeA ? [{
      id: 'theory', label: 'Theory Review', Icon: BookIcon,
      desc: 'Flashcards & quizzes across the Theme A syllabus',
      pct: themeAProgress === null ? null : Math.round(themeAProgress), barColor: 'var(--accent)',
    }] : []),
    {
      id: 'locker', label: 'The Locker', Icon: ChestIcon,
      desc: 'Badges, cosmetics & your stats',
      meta: `${exercisesDone} done`,
    },
  ];

  return (
    <div className="cd-zone-tiles" role="tablist" aria-label="Dashboard sections">
      {tiles.map(tile => {
        const active = activeTab === tile.id;
        return (
          <button
            key={tile.id}
            role="tab"
            aria-selected={active}
            className={`cd-zone-tile${active ? ' cd-zone-tile-active' : ''}`}
            onClick={() => onSelect(tile.id)}
          >
            <div className="cd-zone-tile-top">
              <span className="cd-zone-tile-icon"><tile.Icon size={18} /></span>
              <span className="cd-zone-tile-pct ib-mono">
                {tile.pct !== undefined ? (tile.pct === null ? '—' : `${tile.pct}%`) : tile.meta}
              </span>
            </div>
            <div className="cd-zone-tile-label">{tile.label}</div>
            <div className="cd-zone-tile-desc">{tile.desc}</div>
            {tile.pct !== undefined && (
              <div className="cd-zone-tile-track">
                <div className="cd-zone-tile-fill" style={{ width: `${tile.pct ?? 0}%`, background: tile.barColor }} />
              </div>
            )}
          </button>
        );
      })}

      {/* Not a syllabus tab — a separate tool, so it links out rather than switching panels */}
      {onOpenBits && (
        <button className="cd-zone-tile cd-zone-tile-link" onClick={onOpenBits}>
          <div className="cd-zone-tile-top">
            <span className="cd-zone-tile-icon"><BitsIcon size={18} /></span>
          </div>
          <div className="cd-zone-tile-label">brookebits</div>
          <div className="cd-zone-tile-desc">Your own code scratchpad — jump in anytime</div>
        </button>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function completionBarBackground(pct) {
  if (pct < 50) return 'linear-gradient(90deg, oklch(0.60 0.18 25 / 0.55), oklch(0.60 0.18 25))';
  if (pct < 80) return 'linear-gradient(90deg, oklch(0.72 0.14 65 / 0.55),  oklch(0.72 0.14 65))';
  return            'linear-gradient(90deg, oklch(0.62 0.14 145 / 0.55), oklch(0.62 0.14 145))';
}

function completionTextColor(pct) {
  if (pct < 50) return 'var(--danger)';
  if (pct < 80) return 'var(--warning)';
  return 'var(--success)';
}

// ── Lock icon ─────────────────────────────────────────────────────────────────

function LockIcon({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <rect x="3" y="8" width="10" height="7" rx="1.5" />
      <path d="M5 8V5.5a3 3 0 0 1 6 0V8" />
    </svg>
  );
}

// ── IB Paper 2 Vault teaser ───────────────────────────────────────────────────

function VaultTeaser({ isEnabled = false }) {
  return (
    <div
      className="cd-vault"
      aria-disabled={isEnabled ? undefined : "true"}
      style={isEnabled ? { background: 'oklch(0.22 0.03 240)' } : undefined}
      onClick={() => isEnabled && alert('Entering the IB Paper 2 Exam Vault...')}
    >
      <div className="cd-vault-lock" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 16 16" fill="none"
          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7.5" width="12" height="8" rx="2" />
          <path d="M4.5 7.5V5a3.5 3.5 0 0 1 7 0v2.5" />
          <circle cx="8" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <line x1="8" y1="13.2" x2="8" y2="14.5" strokeWidth="1.4" />
        </svg>
      </div>

      <div className="cd-vault-body">
        <div className="cd-vault-title">The IB Paper 2 Vault</div>
        <div className="cd-vault-sub">
          Requires 100% completion of Theme B to unlock
        </div>
      </div>

      <div className="cd-vault-right">
        <span className="cd-vault-badge">Boss Tier</span>
      </div>
    </div>
  );
}

// ── Syllabus Matrix ───────────────────────────────────────────────────────────

function SyllabusMatrix({ onSelectTopic, topicData, activeLang, activeLevel, paper2Unlocked, playerData, onUpsell }) {
  const visibleTopics = ALL_TOPICS.filter(topic => {
    if (!topic.isAvailable) return true;
    const langs = topic.languages ?? ['python'];
    return langs.includes(activeLang);
  });

  const langLabel = activeLang === 'java' ? 'Java' : 'Python';

  return (
    <div className="cd-card">
      <div className="cd-card-header">
        <LayersIcon size={15} />
        <span className="cd-card-title">Syllabus Matrix</span>
        <div className="cd-legend">
          {[
            { label: '<50%',   color: 'var(--danger)'  },
            { label: '50–80%', color: 'var(--warning)' },
            { label: '80%+',   color: 'var(--success)' },
          ].map(l => (
            <span key={l.label} className="cd-legend-item">
              <span className="cd-legend-dot" style={{ background: l.color }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      <div className="cd-matrix-body">
        {visibleTopics.map(topic => {

          const data = topicData[topic.id];

          const completionPct = data
            ? Math.round((data.completedExercises / data.totalExercises) * 100)
            : 0;
          const hasData  = !!data;
          const barBg    = hasData ? completionBarBackground(completionPct) : 'var(--surface-muted)';
          const barColor = hasData ? completionTextColor(completionPct)     : 'var(--text-muted)';

          const isUnitComplete = hasData && data.totalExercises > 0 && data.completedExercises >= data.totalExercises;
          const masteryDecayed = isUnitComplete && data.masteryScore !== null && data.masteryScore < MASTERY_DECAY_THRESHOLD;
          const masteryPct     = (isUnitComplete && data.masteryScore !== null) ? Math.round(data.masteryScore * 100) : null;
          const isMutedHL      = activeLevel === 'sl' && topic.hlOnly;

          // Paper 2 practice stays locked until every core Theme B unit in the
          // active language track is complete.
          const isP2Locked = isPaper2Topic(topic) && !paper2Unlocked;
          // Freemium: topics outside the free tier are Premium-locked for
          // un-entitled accounts (entitled accounts bypass; Paper 2's completion
          // gate above still applies to them). The server enforces this too (402).
          const isPremiumLocked = topic.isAvailable && !isTopicAccessible(playerData, topic, activeLang);
          const clickable  = topic.isAvailable && !isP2Locked && !isPremiumLocked;
          // Premium-locked rows are still interactive — a click opens the upsell.
          const interactive = clickable || isPremiumLocked;
          const activate = () => {
            if (clickable) onSelectTopic?.(topic);
            else if (isPremiumLocked) onUpsell?.(topic);
          };

          return (
            <div
              key={topic.id}
              className={[
                'cd-matrix-row',
                clickable      ? 'cd-matrix-row-available' : 'cd-matrix-row-locked',
                isPremiumLocked ? 'cd-matrix-row-premium'  : '',
                masteryDecayed ? 'cd-matrix-row-decayed'   : '',
              ].filter(Boolean).join(' ')}
              style={isMutedHL ? { opacity: 0.5 } : undefined}
              onClick={interactive ? activate : undefined}
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={interactive
                ? (e) => { if (e.key === 'Enter' || e.key === ' ') activate(); }
                : undefined}
            >
              <code className="cd-matrix-chip ib-mono">{topic.id}</code>

              <div className="cd-matrix-namecell">
                <span className={`cd-matrix-name ${!clickable ? 'cd-matrix-name-muted' : ''}`}>
                  {topic.title.replace(`${topic.id} `, '')}
                </span>
                {isP2Locked && (
                  <span className="cd-matrix-note">
                    Complete all {langLabel} exercises to unlock
                  </span>
                )}
                {isPremiumLocked && (
                  <span className="cd-matrix-note cd-matrix-note-premium">
                    Premium
                  </span>
                )}
              </div>

              {isMutedHL && (
                <span className="cd-hl-badge">HL Optional</span>
              )}

              <span
                className={`cd-matrix-warn ${masteryDecayed ? 'cd-matrix-warn-visible' : ''}`}
                title={masteryDecayed ? `Mastery at ${masteryPct}% — needs a refresher` : undefined}
                aria-label={masteryDecayed ? `Mastery decayed to ${masteryPct}%` : undefined}
              >
                <TriangleAlertIcon size={13} />
              </span>

              <div className="cd-matrix-right">
                {clickable && (
                  <>
                    <div className="cd-matrix-track">
                      <div className="cd-matrix-bar"
                        style={{ width: `${completionPct}%`, background: barBg }}
                      />
                    </div>
                    <span className="cd-matrix-pct ib-mono" style={{ color: barColor }}>
                      {hasData
                        ? `${data.completedExercises}/${data.totalExercises}`
                        : '—'
                      }
                    </span>
                    <span className="cd-matrix-action" title="Jump in">
                      <PlayIcon size={10} />
                    </span>
                  </>
                )}
                {!clickable && (
                  <span className={`cd-matrix-locked-icon ${isPremiumLocked ? 'cd-matrix-locked-icon-premium' : ''}`.trim()}>
                    <LockIcon size={12} />
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {(() => {
          const totalCoreTasks = visibleTopics.reduce((acc, t) => acc + (topicData[t.id]?.totalExercises || 0), 0);
          const completedCoreTasks = visibleTopics.reduce((acc, t) => acc + (topicData[t.id]?.completedExercises || 0), 0);
          const vaultUnlocked = totalCoreTasks > 0 && completedCoreTasks >= totalCoreTasks;
          return <VaultTeaser isEnabled={vaultUnlocked} />;
        })()}
      </div>
    </div>
  );
}

// ── Needs Review strip ────────────────────────────────────────────────────────

function NeedsReviewCard({ items }) {
  if (items.length === 0) return null;
  return (
    <div className="cd-needs-review">
      <div className="cd-nr-icon-col">
        <div className="cd-nr-icon-tile" aria-hidden="true">
          <ClockIcon size={20} />
        </div>
        <div>
          <div className="cd-nr-eyebrow">
            Needs Review · {items.length} {items.length === 1 ? 'topic' : 'topics'}
          </div>
          <div className="cd-nr-heading">Your mastery has decayed on a few topics</div>
          <div className="cd-nr-sub">A quick review session will lock these back in.</div>
        </div>
      </div>
      <div className="cd-nr-items">
        {items.map(it => (
          <div key={it.id} className="cd-nr-item">
            <code className="cd-nr-chip ib-mono">{it.id}</code>
            <div>
              <div className="cd-nr-item-name">{it.name}</div>
              <div className="cd-nr-item-meta">
                Mastery: {Math.round(it.mastery * 100)}% — knowledge is fading
              </div>
            </div>
          </div>
        ))}
      </div>
      <button className="cd-review-btn">
        Review now <ArrowRightIcon size={12} />
      </button>
    </div>
  );
}

// ── Chevron icon (for accordion) ──────────────────────────────────────────────

function ChevronIcon({ open, size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }}
    >
      <polyline points="3 6 8 11 13 6" />
    </svg>
  );
}

// ── Theory Review Panel — 3-level accordion with multi-select ─────────────────

function TheoryReviewPanel({ activeLevel, onStudy, onReview, themeAProgress, initialScopeReq, playerData, onUpsell }) {
  const [openTopics,    setOpenTopics]    = useState({ A1: true });
  const [openSubtopics, setOpenSubtopics] = useState({});
  // Map<scope → title> — supports multi-select across any statements
  const [selectedMap,   setSelectedMap]   = useState(new Map());
  const [dueCount,      setDueCount]      = useState(null);

  // Deep-link from the nav drawer: expand the requested topic + subtopic.
  // Keyed on the request nonce so re-selecting the same subtopic re-expands it.
  useEffect(() => {
    const scope = initialScopeReq?.scope;
    if (!scope) return;
    const topicId = scope.split('.')[0]; // 'A1.1' → 'A1'
    setOpenTopics(prev => ({ ...prev, [topicId]: true }));
    setOpenSubtopics(prev => ({ ...prev, [scope]: true }));
    // Scroll the subtopic into view once it renders.
    requestAnimationFrame(() => {
      const el = document.getElementById(`cd-tacc-sub-${scope}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [initialScopeReq?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the spaced-repetition due count for the review CTA.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/theme-a/due-cards?level=${activeLevel}`,
          { headers: authHeaders() }
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setDueCount(d.count ?? 0);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [activeLevel]);

  // Deselect HL-only statements when switching to SL
  useEffect(() => {
    if (activeLevel === 'sl') {
      setSelectedMap(prev => {
        const next = new Map(prev);
        THEME_A.forEach(topic => topic.subtopics.forEach(sub => {
          if (sub.hlOnly) sub.statements.forEach(s => next.delete(s.scope));
          else sub.statements.forEach(s => { if (s.hlOnly) next.delete(s.scope); });
        }));
        return next.size === prev.size ? prev : next;
      });
    }
  }, [activeLevel]);

  const toggleTopic = useCallback((id) => {
    setOpenTopics(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleSubtopicOpen = useCallback((scope) => {
    setOpenSubtopics(prev => ({ ...prev, [scope]: !prev[scope] }));
  }, []);

  const toggleStmt = useCallback((scope, title) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (next.has(scope)) next.delete(scope); else next.set(scope, title);
      return next;
    });
  }, []);

  const toggleAllInSub = useCallback((sub, level) => {
    const visible = sub.statements.filter(s => !(level === 'sl' && s.hlOnly));
    setSelectedMap(prev => {
      const next = new Map(prev);
      const allSel = visible.every(s => next.has(s.scope));
      if (allSel) visible.forEach(s => next.delete(s.scope));
      else visible.forEach(s => next.set(s.scope, s.title));
      return next;
    });
  }, []);

  const selectedScopes = [...selectedMap.keys()].sort();
  const selCount = selectedMap.size;
  const ctaTitle = selCount === 0 ? ''
    : selCount === 1 ? [...selectedMap.values()][0]
    : `${selectedScopes[0]} & ${selCount - 1} more`;
  // SQL exercises only exist for the "Construct ..." statements A3.3.2/.3/.4,
  // so the SQL launcher appears only when the selection includes one of them.
  const hasSql = selectedScopes.some(s => /^A3\.3\.[234]\b/.test(s));

  return (
    <div className="cd-theory-panel">

      {/* Mastery summary + spaced-repetition review CTA */}
      <div className="cd-theory-summary">
        <div className="cd-theory-summary-main">
          <div className="cd-theory-summary-label">Theme A overall mastery</div>
          <div className="cd-theory-summary-bar-wrap">
            <div className="cd-theory-summary-bar">
              <div className="cd-theory-summary-fill" style={{ width: `${themeAProgress ?? 0}%` }} />
            </div>
            <span className="cd-matrix-pct ib-mono" style={{ color: 'var(--text-muted)', minWidth: 38 }}>
              {themeAProgress === null ? '—' : `${Math.round(themeAProgress)}%`}
            </span>
          </div>
        </div>
        {dueCount !== null && dueCount > 0 && (
          <button className="cd-review-due-btn" onClick={onReview}>
            🔁 Review <strong>{dueCount}</strong> due
          </button>
        )}
      </div>

      {/* Selection CTA — appears when ≥1 statement selected */}
      {selCount > 0 && (
        <div className="cd-theory-cta-bar">
          <div className="cd-theory-cta-scope">
            <span className="cd-theory-cta-count">{selCount} selected</span>
            <span className="cd-theory-cta-title">{ctaTitle}</span>
          </div>
          <div className="cd-theory-cta-btns">
            <button
              className="action-btn action-run-secondary"
              onClick={() => onStudy('flashcards', selectedScopes, ctaTitle)}
            >
              Flashcards
            </button>
            <button
              className="action-btn action-run-primary"
              onClick={() => onStudy('quiz', selectedScopes, ctaTitle)}
            >
              Quiz
            </button>
            {hasSql && (
              <button
                className="action-btn action-run-primary"
                onClick={() => onStudy('sql', selectedScopes, ctaTitle)}
              >
                SQL
              </button>
            )}
          </div>
        </div>
      )}

      {/* Accordion tree */}
      <div className="cd-theory-accordion">
        {THEME_A.map(topic => {
          // Freemium: theory topics outside the free tier are Premium-locked for
          // un-entitled accounts (server enforces with 402). A locked header opens
          // the upsell instead of expanding.
          const locked = !isTheoryAccessible(playerData, topic.id);
          const topicOpen = !locked && !!openTopics[topic.id];
          const subtopicCount = topic.subtopics.length;
          return (
            <div key={topic.id} className={`cd-tacc-topic${locked ? ' cd-tacc-topic-premium' : ''}`}>

              {/* Topic header */}
              <button
                className="cd-tacc-topic-hdr"
                onClick={() => locked ? onUpsell?.() : toggleTopic(topic.id)}
                aria-expanded={topicOpen}
              >
                <span className="cd-tacc-icon" aria-hidden="true">{topic.icon}</span>
                <span className="cd-tacc-topic-title">{topic.title}</span>
                {locked
                  ? <span className="cd-tacc-count cd-matrix-note-premium">Premium</span>
                  : <span className="cd-tacc-count">{subtopicCount} subtopic{subtopicCount !== 1 ? 's' : ''}</span>}
                {locked ? <LockIcon size={12} /> : <ChevronIcon open={topicOpen} />}
              </button>

              {topicOpen && (
                <div className="cd-tacc-topic-body">
                  {topic.subtopics.map(sub => {
                    const subMuted = activeLevel === 'sl' && sub.hlOnly;
                    const subOpen  = !!openSubtopics[sub.scope];
                    const stmtCount = sub.statements.length;
                    const visibleStmts = sub.statements.filter(s => !(activeLevel === 'sl' && s.hlOnly));
                    const subSelCount  = visibleStmts.filter(s => selectedMap.has(s.scope)).length;
                    const subAllSel    = subSelCount === visibleStmts.length && visibleStmts.length > 0;
                    const subSomeSel   = subSelCount > 0 && !subAllSel;

                    return (
                      <div key={sub.scope} id={`cd-tacc-sub-${sub.scope}`} className={`cd-tacc-sub${subMuted ? ' cd-tacc-muted' : ''}`}>

                        {/* Subtopic header row — checkbox (select-all) + expand toggle */}
                        <div className="cd-tacc-sub-hdr-row">
                          <button
                            className={[
                              'cd-tacc-sub-check-btn',
                              subAllSel  ? 'cd-tacc-check-on'      : '',
                              subSomeSel ? 'cd-tacc-check-partial'  : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => !subMuted && toggleAllInSub(sub, activeLevel)}
                            disabled={subMuted}
                            aria-label={`Select all statements in ${sub.scope}`}
                            aria-pressed={subAllSel || subSomeSel}
                          >
                            {subAllSel  && <span className="cd-tacc-check-mark">✓</span>}
                            {subSomeSel && <span className="cd-tacc-check-mark cd-tacc-check-dash">—</span>}
                          </button>
                          <button
                            className="cd-tacc-sub-expand"
                            onClick={() => !subMuted && toggleSubtopicOpen(sub.scope)}
                            aria-expanded={subOpen}
                            disabled={subMuted}
                          >
                            <code className="cd-matrix-chip ib-mono cd-tacc-sub-chip">{sub.scope}</code>
                            <span className="cd-tacc-sub-title">
                              {sub.title.replace(`${sub.scope} `, '')}
                              {sub.hlOnly && <span className="cd-hl-badge" style={{ marginLeft: 6 }}>HL only</span>}
                            </span>
                            <span className="cd-tacc-count">{stmtCount} statement{stmtCount !== 1 ? 's' : ''}</span>
                            {!subMuted && <ChevronIcon open={subOpen} />}
                          </button>
                        </div>

                        {/* Statement list */}
                        {subOpen && !subMuted && (
                          <div className="cd-tacc-stmt-list">
                            {sub.statements.map(stmt => {
                              const stmtMuted = activeLevel === 'sl' && stmt.hlOnly;
                              const isSelected = selectedMap.has(stmt.scope);
                              return (
                                <button
                                  key={stmt.scope}
                                  className={[
                                    'cd-tacc-stmt',
                                    stmtMuted  ? 'cd-tacc-muted'    : '',
                                    isSelected ? 'cd-tacc-stmt-sel' : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={() => !stmtMuted && toggleStmt(stmt.scope, stmt.title)}
                                  disabled={stmtMuted}
                                  aria-pressed={isSelected}
                                >
                                  <span className={[
                                    'cd-tacc-check',
                                    isSelected ? 'cd-tacc-check-on' : '',
                                  ].filter(Boolean).join(' ')} aria-hidden="true">
                                    {isSelected && <span className="cd-tacc-check-mark">✓</span>}
                                  </span>
                                  <code className="cd-matrix-chip ib-mono cd-tacc-stmt-chip">{stmt.scope}</code>
                                  <span className="cd-tacc-stmt-title">
                                    {stmt.title}
                                    {stmt.hlOnly && !sub.hlOnly && (
                                      <span className="cd-hl-badge" style={{ marginLeft: 6 }}>HL only</span>
                                    )}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Session resume banner ─────────────────────────────────────────────────────

function ResumeBanner({ onResume }) {
  const raw = localStorage.getItem('last_active_exercise');
  if (!raw) return null;
  let name = raw;
  try { name = JSON.parse(raw)?.name || raw; } catch (_) {}
  if (!name) return null;
  return (
    <button className="cd-resume-banner" onClick={onResume}>
      <span className="cd-resume-icon" aria-hidden="true">⚡</span>
      Resume Last Exercise: <strong>{name}</strong>
      <span className="cd-resume-arrow" aria-hidden="true"> →</span>
    </button>
  );
}

// ── CompetencyDashboard page ──────────────────────────────────────────────────

// ── Premium upsell modal ────────────────────────────────────────────────────
// Shown when a free account taps a Premium-locked topic. The server is the real
// boundary (402); this just explains what Premium unlocks. The self-serve checkout
// flow is a later track, so there is no live "buy" button yet — kept honest.
function PremiumUpsellModal({ onClose }) {
  return (
    <div className="cd-upsell-overlay" role="dialog" aria-modal="true" aria-label="Premium" onClick={onClose}>
      <div className="cd-upsell-card" onClick={(e) => e.stopPropagation()}>
        <div className="cd-upsell-icon"><LockIcon size={18} /></div>
        <h2 className="cd-upsell-title">A Premium feature</h2>
        <p className="cd-upsell-body">
          Premium unlocks the full syllabus in both Python and Java, the Paper 2
          exam vault, and saving &amp; sharing your own code snippets.
        </p>
        <UpgradeOptions />
        <button className="cd-upsell-btn" onClick={onClose}>Keep exploring the free units</button>
      </div>
    </div>
  );
}


export default function CompetencyDashboard({
  onBack, onSelectTopic, playerData, displayName, studentId, activeLang, activeLevel = 'hl', onTrackChange, onEquipCosmetic, controlledTab, onTabChange, theoryScopeReq, themeAAccess = false, goHomeSignal, onOpenBits,
}) {
  // Tab is controllable from the parent (the drawer deep-links into tabs);
  // falls back to internal state so the component still works standalone.
  const [internalTab, setInternalTab] = useState('locker');
  const activeTab    = controlledTab ?? internalTab;
  const setActiveTab = onTabChange   ?? setInternalTab;
  // studyView: null | { mode: 'flashcards'|'quiz', scopes: string[], title }
  const [studyView,  setStudyView]  = useState(null);
  // Premium upsell modal — set to a truthy value to show it.
  const [upsellOpen, setUpsellOpen] = useState(false);
  const openUpsell  = useCallback(() => setUpsellOpen(true),  []);
  const closeUpsell = useCallback(() => setUpsellOpen(false), []);

  const handleStudy = useCallback((mode, scopes, title) => {
    setStudyView({ mode, scopes, title });
  }, []);

  const handleReview = useCallback(() => {
    setStudyView({ mode: 'flashcards', scopes: [], title: 'Spaced repetition', reviewMode: true });
  }, []);

  const clearStudyView = useCallback(() => {
    setStudyView(null);
  }, []);

  // "Home" (the Topbar's logo/XP pill) is page-level routing that can't see
  // this component's internal studyView — drop out of it whenever that signal
  // changes, so Home always actually lands on the zone-tile hub.
  useEffect(() => {
    setStudyView(null);
  }, [goHomeSignal]);

  const [themeAOverall, setThemeAOverall] = useState(null);
  useEffect(() => {
    if (!studentId || !themeAAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/theme-a/mastery/${studentId}?level=${activeLevel}`,
          { headers: authHeaders() }
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setThemeAOverall(d?.overall?.percent ?? null);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [studentId, activeLevel, themeAAccess]);
  const themeAProgress = themeAOverall === null ? null : themeAOverall * 100;

  const topicData = useMemo(() => {
    if (!playerData?.units) return {};
    const map = {};
    ALL_TOPICS.forEach(topic => {
      if (!topic.unitId) return;
      const resolvedUnitId =
        activeLang === 'java' && !topic.unitId.endsWith('_java')
          ? `${topic.unitId}_java`
          : topic.unitId;
      const u = playerData.units[resolvedUnitId];
      if (u) {
        const rawMastery = u.masteryScore ?? u.mastery_score;
        map[topic.id] = {
          completedExercises: u.completedExercises ?? u.completed_exercises ?? 0,
          totalExercises:     u.totalExercises     ?? u.total_exercises     ?? 0,
          masteryScore:       rawMastery !== undefined ? rawMastery : null,
        };
      }
    });
    return map;
  }, [playerData, activeLang]);

  const mergedPlayer = useMemo(() => {
    const baseBadgeIds = playerData?.earnedBadgeIds || [];
    const badges = playerData?.polyglot && !baseBadgeIds.includes('polyglot')
      ? [...baseBadgeIds, 'polyglot']
      : baseBadgeIds;

    return {
      ...STUB_PLAYER,
      ...playerData,
      name:            displayName || localStorage.getItem('display_name') || STUB_PLAYER.name,
      lifetimeXP:      playerData?.lifetimeXP      ?? STUB_PLAYER.lifetimeXP,
      globalXP:        playerData?.globalXP        ?? playerData?.lifetimeXP ?? STUB_PLAYER.lifetimeXP,
      stats:           playerData?.stats           ?? STUB_PLAYER.stats,
      earnedBadgeIds:  badges,
    };
  }, [playerData, displayName]);

  const themeBProgress = calcThemeBProgress(topicData);
  const needsReview    = deriveNeedsReview(topicData, ALL_TOPICS);
  const paper2Unlocked = isPaper2Accessible(playerData, activeLang, activeLevel);

  // ── Study view (Flashcards / Quiz / SQL rendered inline, bypassing ThemeAHub) ─
  if (studyView) {
    const StudyScreen = studyView.mode === 'sql'  ? SqlExercise
                      : studyView.mode === 'quiz' ? Quiz
                      : Flashcards;
    return (
      <div className="cd-page">
        <StudyScreen
          scopes={studyView.scopes}
          level={activeLevel}
          scopeTitle={studyView.title}
          reviewMode={studyView.reviewMode}
          onBack={clearStudyView}
        />
      </div>
    );
  }

  return (
    <div className="cd-page">

      {upsellOpen && <PremiumUpsellModal onClose={closeUpsell} />}

      {/* Greeting + course-track chip — replaces the old marketing-style header */}
      <div className="cd-greeting-row">
        <div className="cd-greeting-text">{timeGreeting()}, {mergedPlayer.name}</div>
        <TrackPicker language={activeLang} level={activeLevel} onSave={onTrackChange} />
      </div>

      {/* Shared player profile */}
      <GlobalPlayerCard
        playerData={mergedPlayer}
        themeBProgress={themeBProgress}
        themeAProgress={themeAProgress}
        showThemeA={themeAAccess}
      />

      {/* Session resume banner — a modest nudge, not the loudest thing on the page */}
      <ResumeBanner onResume={onBack} />

      {/* Zone tiles — the three sections, now unmissable instead of a thin pill bar */}
      <ZoneTiles
        activeTab={activeTab}
        onSelect={setActiveTab}
        themeBProgress={themeBProgress}
        themeAProgress={themeAProgress}
        showThemeA={themeAAccess}
        exercisesDone={mergedPlayer.stats?.totalExercises ?? 0}
        onOpenBits={onOpenBits}
      />

      {/* ── Coding Labs tab ─────────────────────────────────────────────────── */}
      {activeTab === 'coding' && (
        <>
          <NeedsReviewCard items={needsReview} />
          <div className="cd-grid">
            <SyllabusMatrix
              onSelectTopic={onSelectTopic}
              topicData={topicData}
              activeLang={activeLang}
              activeLevel={activeLevel}
              paper2Unlocked={paper2Unlocked}
              playerData={playerData}
              onUpsell={openUpsell}
            />
          </div>
        </>
      )}

      {/* ── Theory Review tab ────────────────────────────────────────────────── */}
      {activeTab === 'theory' && (
        <TheoryReviewPanel
          activeLevel={activeLevel}
          onStudy={handleStudy}
          onReview={handleReview}
          themeAProgress={themeAProgress}
          initialScopeReq={theoryScopeReq}
          playerData={playerData}
          onUpsell={openUpsell}
        />
      )}

      {/* ── The Locker tab ───────────────────────────────────────────────────── */}
      {activeTab === 'locker' && (
        <TheLocker playerData={mergedPlayer} onEquipCosmetic={onEquipCosmetic} />
      )}

    </div>
  );
}
